import type { ISetting } from "../../models/Setting";

export const GENERAL_SETTINGS_TAB = "General";

export type SettingTabGroup = {
  tabs: string[];
  grouped: Record<string, ISetting[]>;
};

/**
 * Splits the general settings into tabs by the prefix before the FIRST ':' in each key.
 * Unprefixed keys land on the General tab, which is hidden when empty. Shared by the Admin
 * Settings page and the admin landing page, so both see the same tabs.
 */
export function groupSettingsByPrefix(settings: ISetting[] | undefined): SettingTabGroup {
  const map = new Map<string, ISetting[]>();

  const add = (tab: string, s: ISetting) => {
    if (!map.has(tab)) map.set(tab, []);
    map.get(tab)!.push(s);
  };

  (settings ?? []).forEach((s) => {
    // Part tags are edited in Admin -> Part Tags now; the legacy rows only seed the Default tag.
    if (s.key.startsWith("Tags:")) return;

    // Till:* has its own Admin page (Admin → Till) with proper pickers; listing raw GUIDs here too just confuses.
    if (s.key.startsWith("Till:")) return;
    const idx = s.key.indexOf(":");
    if (idx === -1) add(GENERAL_SETTINGS_TAB, s);
    else {
      const prefix = s.key.slice(0, idx).trim();
      add(prefix.length ? prefix : GENERAL_SETTINGS_TAB, s);
    }
  });

  const general = map.get(GENERAL_SETTINGS_TAB) ?? [];
  const prefixes = Array.from(map.keys())
    .filter((k) => k !== GENERAL_SETTINGS_TAB)
    .sort((a, b) => a.localeCompare(b));

  const tabs = general.length > 0 ? [GENERAL_SETTINGS_TAB, ...prefixes] : prefixes;

  return {
    tabs,
    grouped: Object.fromEntries(map.entries()) as Record<string, ISetting[]>,
  };
}

/** The part of a setting key shown to the user: everything after the prefix. */
export function settingDisplayKey(key: string) {
  const idx = key.indexOf(":");
  return idx === -1 ? key : key.slice(idx + 1).trim();
}

// Prefixes are whatever the API stores, so only the ones that read badly as-is get a label.
const TAB_LABELS: Record<string, string> = {
  Ebay: "eBay",
  EbayImport: "eBay import",
  ListingRefresh: "Listing refresh",
  InvoiceDownload: "Invoice download",
  InitialPriceEstimate: "Initial price estimate",
  ProductionManager: "Production manager",
  ScrapOut: "Scrap-out",
  VehicleLabel: "Vehicle labels",
  WarehouseLabel: "Warehouse labels",
  VehicleLookup: "Vehicle lookup",
  PartsLookup: "Parts lookup",
  UkVehicleData: "UK Vehicle Data",
  OneAutoApi: "One Auto API",
  RmcLookup: "RMC lookup",
  RemoveBG: "Remove BG",
  HtmlToPdf: "HTML to PDF",
  GetPostCodes: "Get Postcodes",
  ExchangeGraph: "Exchange mailbox",
  Rs: "RS",
  Smtp: "SMTP",
  Vat: "VAT",
};

export function settingTabLabel(tab: string) {
  return TAB_LABELS[tab] ?? tab;
}

/** Deep link to one tab of the Admin Settings page. */
export function settingTabHref(tab: string) {
  return `/admin/admin-credentials?tab=${encodeURIComponent(tab)}`;
}
